import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationPanel = () => {
  const { 
    notifications, 
    isOpen, 
    closePanel, 
    markAsRead, 
    markAllAsRead, 
    clearAll,
    unreadCount,
    pushPermission,
    requestPushPermission,
  } = useNotifications();

  if (!isOpen) return null;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message': return 'MessageCircle';
      case 'order': return 'Package';
      case 'bid': return 'Tag';
      case 'success': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      default: return 'Bell';
    }
  };

  const getNotificationLink = (notification) => {
    switch (notification.type) {
      case 'message': return '/order-management';
      case 'order': return '/order-management';
      case 'bid': return '/custom-request';
      case 'info': return '/caterer-discovery';
      default: return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={closePanel}
        aria-hidden="true"
      />
      
      {/* Panel: full-width contained on mobile, dropdown on desktop */}
      <div className="fixed left-4 right-4 top-[5.25rem] max-h-[calc(100dvh-6rem)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:max-h-80 w-auto sm:w-80 md:w-96 bg-card rounded-xl border border-border shadow-lg z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/50">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={18} className="text-primary" />
            <h3 className="font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          {notifications.length > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-primary hover:underline font-medium"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                <Icon name="BellOff" size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const link = getNotificationLink(notification);
                const content = (
                  <div 
                    className={`px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        !notification.read ? 'bg-primary/10' : 'bg-muted'
                      }`}>
                        <Icon 
                          name={getNotificationIcon(notification.type)} 
                          size={16} 
                          className={!notification.read ? 'text-primary' : 'text-muted-foreground'} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-foreground' : 'text-foreground'}`}>
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTime(notification.timestamp)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                      )}
                    </div>
                  </div>
                );

                return link ? (
                  <Link key={notification.id} to={link} onClick={closePanel}>
                    {content}
                  </Link>
                ) : (
                  <div key={notification.id}>{content}</div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer: push + clear all */}
        <div className="px-4 py-2 border-t border-border bg-muted/50 space-y-2">
          {pushPermission !== 'granted' && typeof window !== 'undefined' && window.Notification && (
            <button
              type="button"
              onClick={() => requestPushPermission()}
              className="text-xs text-primary hover:underline w-full text-center block"
            >
              Enable push notifications
            </button>
          )}
          {pushPermission === 'granted' && (
            <p className="text-xs text-muted-foreground text-center">Push notifications on</p>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center block"
            >
              Clear all notifications
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;
